// API service for api.icaseoeste.com.ar with JWT auth
// En desarrollo, por defecto se llama al backend en el mismo host/puerto que REACT_APP_DEV_API_PROXY
// (http://127.0.0.1:3001). Así se evita el proxy de webpack con multipart/form-data, que suele fallar
// con "Error occurred while trying to proxy". Podés forzar otra URL con REACT_APP_API_BASE_URL.

function trimBase(url) {
  return String(url).trim().replace(/\/$/, '');
}

function resolveDefaultBaseUrl() {
  const explicit = process.env.REACT_APP_API_BASE_URL;
  if (explicit != null && String(explicit).trim() !== '') {
    return trimBase(explicit);
  }
  if (process.env.NODE_ENV === 'development') {
    const devTarget =
      process.env.REACT_APP_DEV_API_PROXY ||
      process.env.DEV_API_PROXY ||
      'http://127.0.0.1:3001';
    return trimBase(devTarget);
  }
  return 'https://api.icaseoeste.com.ar';
}

const DEFAULT_BASE_URL = resolveDefaultBaseUrl();
const TOKEN_STORAGE_KEY = 'icase_jwt_token';

function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || null;
  } catch (_) {
    return null;
  }
}

function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    // ignore storage errors
  }
}

function buildHeaders(extraHeaders = {}, withAuth = true, isFormData = false) {
  const headers = { ...extraHeaders };
  
  // No establecer Content-Type para FormData, el navegador lo hará automáticamente con el boundary
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (withAuth) {
    const token = getStoredToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function apiFetch(path, { method = 'GET', body, headers, withAuth = true, baseURL } = {}) {
  const url = `${baseURL || DEFAULT_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const isFormData = body instanceof FormData;
  console.log(`[ApiService] ${method} ${url}`, body && !isFormData ? { body } : (isFormData ? '[FormData]' : ''));
  const response = await fetch(url, {
    method,
    headers: buildHeaders(headers, withAuth, isFormData),
    body: isFormData ? body : (body !== undefined ? JSON.stringify(body) : undefined),
    credentials: 'omit',
  });

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    // Manejo centralizado de no autorizado: limpiar token y redirigir a login
    if (response.status === 401 || response.status === 403) {
      try { setStoredToken(null); } catch (_) {}
      // Redirigir de forma inmediata al login
      if (typeof window !== 'undefined' && window.location) {
        // Evitar bucles si ya estamos en /login
        if (!window.location.pathname.startsWith('/login')) {
          window.location.replace('/login');
        }
      }
    }
    let msg = null;
    if (data && typeof data === 'object') {
      msg = data.message || data.error;
    } else if (typeof data === 'string') {
      const pre = data.match(/<pre>([^<]*)<\/pre>/i);
      if (pre) msg = pre[1].trim();
      else if (!data.includes('<!DOCTYPE') && data.length < 300) msg = data.trim();
    }
    if (!msg && response.status === 404) {
      msg = 'Recurso no encontrado en el servidor (¿API correcta o proxy en desarrollo?)';
    }
    const error = new Error(msg || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
}

async function login({ email, password, username, user, pass, baseURL } = {}) {
  // Flexible credential keys: prefer email/password
  const payload = {
    email: email || username || user,
    password: password || pass,
  };
  if (!payload.email || !payload.password) {
    throw new Error('Credenciales inválidas: se requiere email y password');
  }

  // Adjust endpoint if backend differs
  const data = await apiFetch('/login', {
    method: 'POST',
    body: payload,
    withAuth: false,
    baseURL,
  });

  // Common token keys
  const token = data?.token || data?.jwt || data?.accessToken || null;
  if (!token) {
    throw new Error('El servicio no devolvió un token JWT');
  }
  setStoredToken(token);
  return { token, user: data?.user || null, raw: data };
}

function logout() {
  setStoredToken(null);
}

function getToken() {
  return getStoredToken();
}

function setToken(token) {
  setStoredToken(token);
}

function get(path, opts) {
  return apiFetch(path, { ...opts, method: 'GET' });
}

function post(path, body, opts) {
  return apiFetch(path, { ...opts, method: 'POST', body });
}

function put(path, body, opts) {
  return apiFetch(path, { ...opts, method: 'PUT', body });
}

function patch(path, body, opts) {
  return apiFetch(path, { ...opts, method: 'PATCH', body });
}

function del(path, opts) {
  return apiFetch(path, { ...opts, method: 'DELETE' });
}

const ApiService = {
  login,
  logout,
  getToken,
  setToken,
  get,
  post,
  put,
  patch,
  del,
  fetch: apiFetch,
};

export default ApiService;


